import { Map, AlertCircle, CheckCircle2, Circle, Clock } from 'lucide-react'
import { FreshnessRibbon } from '@/components/FreshnessRibbon'
import { useCatalogSummary } from '@/hooks/useCatalog'
import { cn } from '@/lib/utils'
import type { TierItem, TierItemStatus, CatalogObjectKpi } from '@/types/api'

function formatPercentage(value: number): string {
  return `${Math.round(value * 100)}%`
}

function formatAge(ageMs: number | null): string {
  if (ageMs == null) return '—'
  const min = Math.round(ageMs / 60000)
  if (min < 60) return `${min}分前`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr}時間前`
  return `${Math.round(hr / 24)}日前`
}

const TIER_STATUS_LABEL: Record<TierItemStatus, string> = {
  active: '稼働',
  ready: '準備済',
  missing: '未対応',
}

const TIER_STATUS_STYLE: Record<TierItemStatus, string> = {
  active: 'bg-green-50 text-green-700 border-green-200',
  ready: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  missing: 'bg-red-50 text-red-700 border-red-200',
}

function TierStatusBadge({ status }: { status: TierItemStatus }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium',
        TIER_STATUS_STYLE[status]
      )}
    >
      {TIER_STATUS_LABEL[status]}
    </span>
  )
}

function TierItemList({ items, emptyLabel }: { items: TierItem[]; emptyLabel: string }) {
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyLabel}</p>
  }
  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item.label} className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium">{item.label}</p>
            {item.note && <p className="text-xs text-muted-foreground">{item.note}</p>}
          </div>
          <TierStatusBadge status={item.status} />
        </li>
      ))}
    </ul>
  )
}

function ObjectRow({ object }: { object: CatalogObjectKpi }) {
  return (
    <tr className="border-b last:border-b-0">
      <td className="py-2 pr-4 text-sm font-medium">{object.sobjectApiName}</td>
      <td className="py-2 pr-4">
        {object.covered ? (
          <span className="inline-flex items-center gap-1 text-sm text-green-700">
            <CheckCircle2 className="h-4 w-4" />
            掌握済
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-sm text-red-700">
            <Circle className="h-4 w-4" />
            未掌握
          </span>
        )}
      </td>
      <td className="py-2 text-sm text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <Clock className="h-3.5 w-3.5" />
          {formatAge(object.ageMs)}
        </span>
      </td>
    </tr>
  )
}

export function MasteryMapPage() {
  const { data, isLoading, error } = useCatalogSummary()

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Map className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold">掌握マップ</h1>
        </div>
        <p className="text-muted-foreground">
          どの object をどこまで掌握しているかを可視化します（集計値のみ・read-only）
        </p>
      </div>

      <FreshnessRibbon kinds={['catalog']} />

      {isLoading && <div className="text-sm text-muted-foreground">読み込み中...</div>}

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <AlertCircle className="h-5 w-5" />
          掌握データの取得に失敗しました
        </div>
      )}

      {data && (
        <>
          {/* 全体掌握率 */}
          <div className="rounded-lg border bg-card p-6 shadow-sm">
            <p className="text-sm text-muted-foreground">全体掌握率</p>
            <p className="mt-1 text-4xl font-bold text-primary">
              {formatPercentage(data.overallCoverage)}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Tier A: {data.tierA.coveredCount}/{data.tierA.expectedCount} object
            </p>
          </div>

          {/* Tier A — catalog all */}
          <div className="rounded-lg border bg-card p-6 shadow-sm">
            <h2 className="mb-4 font-semibold">Tier A — catalog all</h2>
            {data.tierA.perObject.length === 0 ? (
              <p className="text-sm text-muted-foreground">掌握対象の object がまだありません</p>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="pb-2 pr-4 font-medium">object</th>
                    <th className="pb-2 pr-4 font-medium">掌握</th>
                    <th className="pb-2 font-medium">鮮度</th>
                  </tr>
                </thead>
                <tbody>
                  {data.tierA.perObject.map((obj) => (
                    <ObjectRow key={obj.sobjectApiName} object={obj} />
                  ))}
                </tbody>
              </table>
            )}

            {data.tierA.missingObjects.length > 0 && (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                <p className="font-medium">未掌握 object</p>
                <p className="mt-1">{data.tierA.missingObjects.join(', ')}</p>
              </div>
            )}
          </div>

          {/* Tier B / Tier C */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border bg-card p-6 shadow-sm">
              <h2 className="mb-4 font-semibold">Tier B — selective memory</h2>
              <TierItemList items={data.tierB} emptyLabel="まだ対象がありません" />
            </div>
            <div className="rounded-lg border bg-card p-6 shadow-sm">
              <h2 className="mb-4 font-semibold">Tier C — cross-system relation</h2>
              <TierItemList items={data.tierC} emptyLabel="まだ対象がありません" />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
